import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FileText, Layers, Search, BarChart2, GitBranch, Image, Settings, ChevronRight, ArrowRight, Check, Zap, RefreshCw, MousePointer, Edit3, Eye, Upload, Code2, Sparkles, Menu, X } from "lucide-react";
import Icon from '../../components/AppIcon';


// ─── Navbar ───────────────────────────────────────────────────────────────────
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/98 backdrop-blur-md shadow-sm border-b border-slate-200" : "bg-white/80 backdrop-blur-sm border-b border-slate-100"}`}>
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <FileText size={16} className="text-white" />
          </div>
          <span className="font-bold text-lg text-slate-900 font-headline">webpages123.com</span>
        </div>

        <div className="hidden md:flex items-center gap-8">
          {["Features", "Workflow"]?.map((item) => (
            <a key={item} href={`#${item?.toLowerCase()}`} className="text-sm text-slate-600 hover:text-slate-900 transition-colors font-medium">
              {item}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors px-4 py-2">
            Sign in
          </Link>
          <Link to="/login" className="text-sm font-semibold bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Get started free
          </Link>
        </div>

        <button className="md:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X size={20} className="text-slate-700" /> : <Menu size={20} className="text-slate-700" />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden bg-white border-b border-slate-200 px-6 py-4 flex flex-col gap-4">
          {["Features", "Workflow"]?.map((item) => (
            <a key={item} href={`#${item?.toLowerCase()}`} className="text-sm font-medium text-slate-700" onClick={() => setMobileOpen(false)}>
              {item}
            </a>
          ))}
          <Link to="/login" className="text-sm font-semibold bg-blue-600 text-white px-5 py-2.5 rounded-lg text-center">
            Get started free
          </Link>
        </div>
      )}
    </nav>
  );
};

// ─── Hero ─────────────────────────────────────────────────────────────────────
const Hero = () => (
  <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50">
    {/* Animated gradient blobs */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-200/50 blur-[120px] animate-pulse" />
      <div className="absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full bg-indigo-200/40 blur-[100px] animate-pulse" style={{ animationDelay: "1s" }} />
      <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] rounded-full bg-cyan-200/30 blur-[80px] animate-pulse" style={{ animationDelay: "2s" }} />
      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(#94a3b8 1px, transparent 1px), linear-gradient(90deg, #94a3b8 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-16 w-full">
      <div className="max-w-4xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 border border-blue-200 mb-8">
          <Sparkles size={13} className="text-blue-600" />
          <span className="text-xs font-medium text-blue-700">The modern CMS for content teams</span>
        </div>

        <h1 className="text-5xl md:text-7xl font-bold text-slate-900 leading-[1.05] tracking-tight mb-6 font-headline">
          Build websites.<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-500">
            Ship content fast.
          </span>
        </h1>

        <p className="text-lg md:text-xl text-slate-600 max-w-2xl mb-10 leading-relaxed">
          webpages123.com is a full-stack CMS that gives your team a rich editor, live preview, SEO tools, analytics, and version control — all in one place. No plugins. No duct tape.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/login" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5">
            Start building for free
            <ArrowRight size={16} />
          </Link>
          <a href="#workflow" className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-medium px-7 py-3.5 rounded-xl transition-all duration-200 shadow-sm">
            See how it works
            <ChevronRight size={16} />
          </a>
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-6 text-sm text-slate-500">
          {["No credit card required", "Free 14-day trial", "Cancel anytime"]?.map((t) => (
            <span key={t} className="flex items-center gap-2">
              <Check size={14} className="text-emerald-500" />
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Dashboard preview card */}
      <div className="mt-16 relative">
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-2xl shadow-slate-200/80">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-yellow-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
            <span className="ml-3 text-xs text-slate-400 font-accent">webpages123.com/dashboard</span>
          </div>
          <div className="grid grid-cols-12 min-h-[320px]">
            {/* Sidebar mock */}
            <div className="col-span-2 border-r border-slate-100 bg-slate-50 p-3 hidden md:block">
              {[FileText, Layers, Image, Search, BarChart2, GitBranch, Settings]?.map((Icon, i) => (
                <div key={i} className={`flex items-center gap-2 px-2 py-2 rounded-lg mb-1 ${i === 0 ? "bg-blue-100 text-blue-600" : "text-slate-400 hover:text-slate-600"}`}>
                  <Icon size={14} />
                  <div className={`h-1.5 rounded-full flex-1 ${i === 0 ? "bg-blue-300" : "bg-slate-200"}`} style={{ width: `${40 + Math.random() * 40}%` }} />
                </div>
              ))}
            </div>
            {/* Main content mock */}
            <div className="col-span-12 md:col-span-10 p-5 bg-white">
              <div className="flex items-center justify-between mb-4">
                <div className="h-5 w-32 bg-slate-200 rounded" />
                <div className="flex gap-2">
                  <div className="h-7 w-20 bg-blue-100 rounded-lg" />
                  <div className="h-7 w-16 bg-slate-100 rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[["Pages", "24", "text-blue-600"], ["Published", "18", "text-emerald-600"], ["Drafts", "6", "text-amber-600"]]?.map(([label, val, color]) => (
                  <div key={label} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <div className={`text-xl font-bold font-headline ${color}`}>{val}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {[90, 65, 80, 45]?.map((w, i) => (
                  <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-100">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    <div className="h-2 rounded flex-1 bg-slate-200" style={{ maxWidth: `${w}%` }} />
                    <div className="h-5 w-14 rounded-md bg-emerald-100 text-emerald-600 text-xs flex items-center justify-center">Published</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-px left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
      </div>
    </div>
  </section>
);

// ─── Features Bento ───────────────────────────────────────────────────────────
const features = [
  {
    icon: Edit3,
    title: "Rich Text Editor",
    desc: "WYSIWYG editor with real-time collaboration, inline comments, and auto-save drafts. Write once, publish everywhere.",
    span: "col-span-12 md:col-span-7",
    accent: "blue",
    big: true,
  },
  {
    icon: Eye,
    title: "Live Page Preview",
    desc: "See exactly how your content looks on desktop, tablet, and mobile before hitting publish.",
    span: "col-span-12 md:col-span-5",
    accent: "cyan",
  },
  {
    icon: Search,
    title: "SEO Studio",
    desc: "Built-in meta tag manager, sitemap generator, schema markup, keyword analyzer, and robots.txt editor.",
    span: "col-span-12 md:col-span-4",
    accent: "violet",
  },
  {
    icon: BarChart2,
    title: "Analytics Hub",
    desc: "Traffic sources, conversion funnels, real-time visitors, and top-performing pages — no third-party setup needed.",
    span: "col-span-12 md:col-span-4",
    accent: "emerald",
  },
  {
    icon: GitBranch,
    title: "Version Control",
    desc: "Full content versioning with one-click rollback and a complete audit log of every change.",
    span: "col-span-12 md:col-span-4",
    accent: "amber",
  },
  {
    icon: Layers,
    title: "Template Library",
    desc: "Start from professionally designed templates. Customize layouts, components, and styles without writing CSS.",
    span: "col-span-12 md:col-span-5",
    accent: "pink",
  },
  {
    icon: Image,
    title: "Media Library",
    desc: "Drag-and-drop uploads, automatic image optimization, and organized asset management.",
    span: "col-span-12 md:col-span-7",
    accent: "orange",
    big: true,
  },
];

const accentMap = {
  blue: { bg: "bg-blue-50", icon: "text-blue-600", border: "border-blue-200", hover: "group-hover:bg-blue-100" },
  cyan: { bg: "bg-cyan-50", icon: "text-cyan-600", border: "border-cyan-200", hover: "group-hover:bg-cyan-100" },
  violet: { bg: "bg-violet-50", icon: "text-violet-600", border: "border-violet-200", hover: "group-hover:bg-violet-100" },
  emerald: { bg: "bg-emerald-50", icon: "text-emerald-600", border: "border-emerald-200", hover: "group-hover:bg-emerald-100" },
  amber: { bg: "bg-amber-50", icon: "text-amber-600", border: "border-amber-200", hover: "group-hover:bg-amber-100" },
  pink: { bg: "bg-pink-50", icon: "text-pink-600", border: "border-pink-200", hover: "group-hover:bg-pink-100" },
  orange: { bg: "bg-orange-50", icon: "text-orange-600", border: "border-orange-200", hover: "group-hover:bg-orange-100" },
};

const Features = () => (
  <section id="features" className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6">
      <div className="mb-14">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 border border-blue-200 mb-5">
          <Zap size={13} className="text-blue-600" />
          <span className="text-xs font-medium text-blue-700">Everything in one platform</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-headline leading-tight max-w-2xl">
          Every tool your content team needs
        </h2>
        <p className="mt-4 text-slate-600 text-lg max-w-xl">
          Stop juggling five different tools. webpages123.com brings editing, SEO, analytics, and deployment into a single cohesive workspace.
        </p>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {features?.map((f) => {
          const colors = accentMap?.[f?.accent];
          const Icon = f?.icon;
          return (
            <div key={f?.title} className={`${f?.span} group relative rounded-2xl border border-slate-200 bg-slate-50 hover:bg-white p-6 transition-all duration-300 hover:border-slate-300 hover:shadow-md overflow-hidden`}>
              <div className="relative">
                <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${colors?.bg} border ${colors?.border} mb-4`}>
                  <Icon size={18} className={colors?.icon} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 font-headline mb-2">{f?.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{f?.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// ─── Workflow ─────────────────────────────────────────────────────────────────
const workflowSteps = [
  {
    step: "01",
    icon: MousePointer,
    title: "Pick a template or start blank",
    desc: "Browse the Template Library for pre-built page layouts — landing pages, blogs, portfolios, product pages. Or open a blank canvas and build from scratch.",
    detail: "Template Library → New Page",
    color: "blue",
  },
  {
    step: "02",
    icon: Edit3,
    title: "Write and structure your content",
    desc: "Use the WYSIWYG rich text editor to add headings, body copy, images, embeds, and code blocks. Formatting is applied visually — no HTML required.",
    detail: "Content Editor → Rich Text",
    color: "cyan",
  },
  {
    step: "03",
    icon: Eye,
    title: "Preview across devices",
    desc: "Switch between desktop, tablet, and mobile views in the live preview panel. See exactly how your page renders before anyone else does.",
    detail: "Content Editor → Preview Panel",
    color: "violet",
  },
  {
    step: "04",
    icon: Search,
    title: "Optimize for search engines",
    desc: "Open SEO Studio to set meta title, description, Open Graph tags, and structured data. The SEO score card shows what to fix before you publish.",
    detail: "SEO Studio → Meta Tags + Score",
    color: "emerald",
  },
  {
    step: "05",
    icon: Upload,
    title: "Publish or schedule",
    desc: "Hit Publish to make the page live instantly, or set a future date and time. webpages123.com handles the deployment — no FTP, no build commands.",
    detail: "Page Manager → Publish",
    color: "amber",
  },
  {
    step: "06",
    icon: BarChart2,
    title: "Track performance",
    desc: "After publishing, Analytics Hub shows real-time traffic, bounce rate, top referrers, and conversion events. Iterate based on what the data tells you.",
    detail: "Analytics Hub → Dashboard",
    color: "pink",
  },
];

const stepColorMap = {
  blue: { num: "text-blue-400", bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-600", line: "bg-blue-200" },
  cyan: { num: "text-cyan-400", bg: "bg-cyan-50", border: "border-cyan-200", icon: "text-cyan-600", line: "bg-cyan-200" },
  violet: { num: "text-violet-400", bg: "bg-violet-50", border: "border-violet-200", icon: "text-violet-600", line: "bg-violet-200" },
  emerald: { num: "text-emerald-400", bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-600", line: "bg-emerald-200" },
  amber: { num: "text-amber-400", bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-600", line: "bg-amber-200" },
  pink: { num: "text-pink-400", bg: "bg-pink-50", border: "border-pink-200", icon: "text-pink-600", line: "bg-pink-200" },
};

const Workflow = () => (
  <section id="workflow" className="py-24 bg-slate-50">
    <div className="max-w-7xl mx-auto px-6">
      <div className="mb-16 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 border border-blue-200 mb-5">
          <RefreshCw size={13} className="text-blue-600" />
          <span className="text-xs font-medium text-blue-700">End-to-end workflow</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-headline leading-tight">
          From idea to live page in 6 steps
        </h2>
        <p className="mt-4 text-slate-600 text-lg max-w-xl mx-auto">
          Here's exactly how a website gets built inside webpages123.com — from the first keystroke to a published, indexed page.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {workflowSteps?.map((s, idx) => {
          const colors = stepColorMap?.[s?.color];
          const Icon = s?.icon;
          return (
            <div key={s?.step} className="relative group rounded-2xl border border-slate-200 bg-white hover:shadow-md p-6 transition-all duration-300 hover:border-slate-300">
              <div className="flex items-start justify-between mb-5">
                <span className={`text-4xl font-bold font-headline ${colors?.num} opacity-50`}>{s?.step}</span>
                <div className={`w-10 h-10 rounded-xl ${colors?.bg} border ${colors?.border} flex items-center justify-center`}>
                  <Icon size={18} className={colors?.icon} />
                </div>
              </div>
              <h3 className="text-base font-semibold text-slate-900 font-headline mb-2">{s?.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-4">{s?.desc}</p>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${colors?.bg} border ${colors?.border}`}>
                <Code2 size={11} className={colors?.icon} />
                <span className={`text-xs font-medium font-accent ${colors?.icon}`}>{s?.detail}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// ─── Stats ────────────────────────────────────────────────────────────────────
const Stats = () => (
  <section className="py-16 bg-blue-600 border-y border-blue-500">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {[
          { val: "10k+", label: "Pages published" },
          { val: "99.9%", label: "Uptime SLA" },
          { val: "< 2s", label: "Avg. page load" },
          { val: "6 tools", label: "In one platform" },
        ]?.map(({ val, label }) => (
          <div key={label}>
            <div className="text-3xl md:text-4xl font-bold text-white font-headline">{val}</div>
            <div className="text-sm text-blue-100 mt-1">{label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── CTA ──────────────────────────────────────────────────────────────────────
const CTA = () => (
  <section className="py-24 bg-gradient-to-br from-blue-50 via-indigo-50 to-white relative overflow-hidden">
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-blue-200/40 blur-[100px]" />
    </div>
    <div className="relative max-w-3xl mx-auto px-6 text-center">
      <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-headline mb-5 leading-tight">
        Ready to build your first page?
      </h2>
      <p className="text-slate-600 text-lg mb-10">
        Join content teams who ship faster with webpages123.com. Your first 14 days are free — no card required.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/login" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3.5 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-blue-300 hover:-translate-y-0.5">
          Start building for free
          <ArrowRight size={16} />
        </Link>
        <Link to="/login" className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-medium px-8 py-3.5 rounded-xl transition-all duration-200 shadow-sm">
          Sign in to your account
        </Link>
      </div>
    </div>
  </section>
);

// ─── Footer ───────────────────────────────────────────────────────────────────
const Footer = () => (
  <footer className="bg-slate-900 border-t border-slate-800 py-10">
    <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
          <FileText size={13} className="text-white" />
        </div>
        <span className="font-bold text-white font-headline">webpages123.com</span>
      </div>
      <div className="flex items-center gap-6 text-sm text-slate-400">
        <a href="#features" className="hover:text-white transition-colors">Features</a>
        <a href="#workflow" className="hover:text-white transition-colors">Workflow</a>
        <Link to="/login" className="hover:text-white transition-colors">Sign in</Link>
      </div>
      <div className="text-xs text-slate-500">© 2026 webpages123.com. All rights reserved.</div>
    </div>
  </footer>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
const HomePage = () => (
  <div className="min-h-screen">
    <Navbar />
    <Hero />
    <Stats />
    <Features />
    <Workflow />
    <CTA />
    <Footer />
  </div>
);

export default HomePage;
